import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Currency, CurrencyListItem, SettingsCategory, SettingsName, currencyList } from '@received/pricing-model';
import { EmptyStateCard, Icon, Select } from '../../components';
import { Store } from '../../types/storeTypes';
import { AccountTabs, AuthorizedPage, Sections, settingsNestedPages, resolveActiveTabFromParams } from './Settings.utils';
import { isEmbedded } from '../../services/embedded/embeddedService';
import { images } from '../../constants/images';
import { setAppCurrency, setSidebarIsOpen } from '../../storeSlices/generalSlice';
import { setOpenSuccessErrorModal } from '../../storeSlices/errorSuccessSlice';
import { successErrorMassageOptions } from '../../components/SuccessErrorModal/SuccessErrorModal.utils';
import { PathsConfig, httpService } from '../../services';
import { PermissionsMapping } from '../../services/PermissionsMapping/PermissionsMapping';
import styles from './Settings.module.scss';
import { NodeEnvTypes } from '../../types/generalTypes';

export const Settings = () => {
	const { t } = useTranslation('translation');
	const [activeTab, setActiveTab] = useState({ activeSection: Sections.ACCOUNT, activeButton: AccountTabs.PERSONAL_INFO });
	const [filteredSettings, setFilteredSettings] = useState<AuthorizedPage[]>([]);

	const userRole = useSelector((store: Store) => store.user.role);
	const { isSidebarOpen, appCurrency } = useSelector((store: Store) => store.general);
	const dispatch = useDispatch();
	const location: any = useLocation();
	const [searchParams] = useSearchParams();
	const embeddedPage = isEmbedded();

	useEffect(() => {
		// Query params (`?section=company&tab=company-info`) take precedence so the page is
		// deep-linkable/bookmarkable; fall back to in-app router state, then the default tab.
		const fromParams = resolveActiveTabFromParams(searchParams.get('section'), searchParams.get('tab'));
		if (fromParams) {
			setActiveTab(fromParams);
		} else if (location?.state?.activeSection) {
			setActiveTab({ activeSection: location?.state?.activeSection, activeButton: location?.state?.activeButton });
		}
	}, [searchParams]);

	useEffect(() => {
		!isSidebarOpen && dispatch(setSidebarIsOpen(true));
	}, [isSidebarOpen]);

	useEffect(() => {
		let settingsList = settingsNestedPages;
		if (window._env_.NODE_ENV == NodeEnvTypes.PRODUCTION) {
			settingsList = settingsNestedPages.filter((item) => !item.requiredRole || item.requiredRole === userRole);
		}
		setFilteredSettings(settingsList);
	}, [userRole]);

	const onUpdateCurrency = async (value: Currency) => {
		try {
			await httpService({
				dispatch,
				path: PathsConfig.updateSettingsDataByAttributeName,
				data: { category: SettingsCategory.COMMON, settingName: SettingsName.DEFAULT_CURRENCY, value: value },
			});

			dispatch(setAppCurrency(value));
		} catch (error) {
			dispatch(setOpenSuccessErrorModal({ responseError: error, successErrorMassage: successErrorMassageOptions.ERROR.FAILED_UPDATE_CURRENCY }));
		}
	};

	return (
		<div className={styles.globalPageContainer}>
			<div className={clsx(styles.pageContainer, embeddedPage && styles.pageContainerEmbedded)}>
				{!embeddedPage && (
					<header className={styles.pageHeader}>
						<div>
							<h1>{t('settings')}</h1>
							<span className={styles.description}>{t('settingsDescription')}</span>
						</div>
						<Select
							fullBorder
							className={styles.entitySelect}
							defaultValue={appCurrency}
							onChange={(value: any) => onUpdateCurrency(value)}
							width={14}
							headerStyle={styles.selectHeaderStyle}
							data={currencyList.map((item: CurrencyListItem) => ({
								label: `${item.flag} ${item.currency}`,
								value: item.currency,
							}))}
							required
						/>
					</header>
				)}

				<div className={clsx(styles.settingsContainer, embeddedPage && styles.settingsContainerEmbedded)}>
					{!embeddedPage && (
					<div className={styles.settingsList}>
						{filteredSettings.length ? (
							filteredSettings.map((page, index) => (
								<div key={index} className={styles.listContainer}>
									<div className={styles.listHeader}>
										<span className={styles.listTitle}>{t(page.header)}</span>
										<span className={styles.listDescription}>{t(page.description)}</span>
									</div>

									{page.items.map((item, itemIndex) => {
										const active = activeTab.activeSection === page.section && activeTab.activeButton === itemIndex;
										return item.actionType ? (
											<PermissionsMapping key={itemIndex} actionType={item.actionType}>
												<div
													onClick={() => setActiveTab({ activeSection: page.section, activeButton: itemIndex })}
													className={clsx(styles.settingsButton, active && styles.settingsButtonSelected)}
												>
													{item?.iconImgType && <Icon color={active ? 'primary' : 'neutral700'} imgType={item.iconImgType} height={1.8}></Icon>}
													<div>{t(item.label)}</div>
												</div>
											</PermissionsMapping>
										) : (
											<div
												key={itemIndex}
												onClick={() => setActiveTab({ activeSection: page.section, activeButton: itemIndex })}
												className={clsx(styles.settingsButton, active && styles.settingsButtonSelected)}
											>
												{item?.iconImgType && <Icon color='neutral700' imgType={item.iconImgType} height={1.8}></Icon>}
												<div>{t(item.label)}</div>
											</div>
										);
									})}
								</div>
							))
						) : (
							<div className={styles.emptyContainer}>
								<EmptyStateCard src={images.wip} imgAlt='wip' title={t('wip')} />
							</div>
						)}
					</div>
					)}

					<div className={clsx(styles.settingsCard, embeddedPage && styles.settingsCardEmbedded)}>
						{filteredSettings.length ? (
							filteredSettings
								.find((page) => page.section === activeTab.activeSection)
								?.items?.[activeTab.activeButton]?.component({
									chosenPlatform: location?.state?.chosenPlatform,
								})
						) : (
							<div className={styles.emptyContainer}>
								<EmptyStateCard src={images.wip} imgAlt='wip' title={t('wip')} />
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};
